import React, { useState, useMemo } from 'react';

import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  IconArrowHead,
} from '../../../../../components';
import { createResourceLocatorString } from '../../../../../util/routes';
import MegaMenuSearch from './MegaMenuSearch';

import css from './MegaMenu.module.css';

/**
 * Categories mega dropdown menu component
 * Shows a wide panel with category options that redirects to search page
 */
const CategoriesMegaMenu = ({ currentPage, intl, history, routeConfiguration, isOpen, onRequestOpen, onRequestClose }) => {

  const getCurrentPageClass = page => {
    return currentPage === page ? css.currentPage : null;
  };

  // Smart home categories data - expanded list
  const allCategories = [
    { name: 'Éclairage', value: 'lights', icon: '💡' },
    { name: 'Caméras de sécurité', value: 'security-cameras', icon: '📹' },
    { name: 'Sonnettes connectées', value: 'doorbells', icon: '🚪' },
    { name: 'Capteurs IoT', value: 'sensors', icon: '📡' },
    { name: 'Serrures Connectées', value: 'smart-locks', icon: '🔒' },
    { name: 'Stores & Rideaux', value: 'blind-and-curtains', icon: '🪟' },
    { name: 'Systèmes d\'alarme', value: 'alarm-systems', icon: '🚨' },
    { name: 'Têtes thermostatiques connectées', value: 'radiator-thermostats', icon: '🎛️' },
    { name: 'Thermostats connectés', value: 'smart-thermostats', icon: '🌡️' },
    { name: 'Prises connectées', value: 'smart-plugs', icon: '🔌' },
    { name: 'Babyphones', value: 'baby-monitor', icon: '👶' },
    { name: 'Robots tondeuses', value: 'lawn-mowers', icon: '🌿' },
    {
      name: 'Robots de piscine',
      value: 'robots-de-piscine',
      icon: '🏊',
      path: '/products/robots-de-piscine',
    },
    { name: 'Enceintes et écrans', value: 'smart-speakers-and-screens', icon: '🔊' },
    { name: 'Enregistreurs vidéo réseau (NVR)', value: 'network-video-recorders', icon: '💽' },
  ];

  const [searchTerm, setSearchTerm] = useState('');

  // Filter categories based on search term
  const filteredCategories = useMemo(() => {
    if (!searchTerm.trim()) {
      return allCategories;
    }

    const searchLower = searchTerm.toLowerCase();
    return allCategories.filter(category =>
      category.name.toLowerCase().includes(searchLower) ||
      category.value.toLowerCase().includes(searchLower)
    );
  }, [searchTerm]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleCategoryClick = category => {
    // Some categories define their own dedicated landing path
    if (category?.path) {
      history.push(category.path);
      onRequestClose && onRequestClose();
      return;
    }

    const categoryValue = category?.value;
    if (!categoryValue || typeof categoryValue !== 'string') {
      return;
    }

    // Redirect to search page with category filter (query param)
    const searchParams = { category: categoryValue };
    const to = createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams);
    history.push(to);
    onRequestClose && onRequestClose();
  };

  return (
    <Menu
      contentPlacementOffset={-12}
      contentPosition="left"
      isOpen={isOpen}
      onToggleActive={nextOpen => (nextOpen ? onRequestOpen && onRequestOpen() : onRequestClose && onRequestClose())}
    >
      <MenuLabel
        className={css.megaMenuLabel}
        isOpenClassName={css.megaMenuIsOpen}
        onMouseEnter={() => onRequestOpen && onRequestOpen()}
        onMouseLeave={() => onRequestClose && onRequestClose()}
      >
        <span className={css.megaMenuLabelWrapper}>
          {intl.formatMessage({ id: 'TopbarDesktop.categories.label' })}
          <IconArrowHead
            direction="down"
            size="small"
            rootClassName={css.arrowIcon}
            ariaLabel={intl.formatMessage({ id: 'TopbarDesktop.categories.label' })}
          />
        </span>
      </MenuLabel>
      <MenuContent
        className={css.megaMenuContent}
        onMouseEnter={() => onRequestOpen && onRequestOpen()}
        onMouseLeave={() => onRequestClose && onRequestClose()}
      >
        <MenuItem key="categories-header" className={css.megaMenuHeaderItem}>
          <div className={css.megaMenuHeader}>
            <div>
              <h3 className={css.megaMenuTitle}>
                {intl.formatMessage({ id: 'TopbarDesktop.categories.title' })}
              </h3>
              <p className={css.megaMenuSubtitle}>
                {intl.formatMessage({ id: 'TopbarDesktop.categories.subtitle' })}
              </p>
            </div>
            <NamedLink
              className={css.viewAllLink}
              name="CategoriesPage"
            >
              {intl.formatMessage({ id: 'TopbarDesktop.categories.viewAll' })}
            </NamedLink>
          </div>
        </MenuItem>

        <MenuItem key="categories-search" className={css.megaMenuSearchItem}>
          <MegaMenuSearch
            inputId="mega-menu-categories-search"
            inputName="categories-search"
            placeholder="Rechercher des catégories..."
            onSearch={handleSearch}
          />
        </MenuItem>

        <MenuItem key="categories-grid" className={css.megaMenuGridWrapper}>
          <div className={css.megaMenuGrid}>
            {filteredCategories.map((category, index) => (
              <button
                key={`${category.value}_${index}`}
                className={css.megaMenuItem}
                onClick={() => handleCategoryClick(category)}
              >
                <div className={css.brandLogoContainer}>
                  <span className={css.brandFallback}>
                    {category.icon}
                  </span>
                </div>
                <span className={css.categoryNameWrap}>{category.name}</span>
              </button>
            ))}
          </div>
          {filteredCategories.length === 0 && (
            <div className={css.noResults}>
              <p>Aucune catégorie trouvée pour « {searchTerm} »</p>
            </div>
          )}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

export default CategoriesMegaMenu;
